import { c as _c } from "react/compiler-runtime";
import { Box } from '@mui/material';
import { ChevronRightIcon } from '@/components/icons/ChevronRightIcon';
import { BalanceSheetCellPosition, BalanceSheetCellStyle, BalanceSheetDensity, BalanceSheetSummaryVariant } from '../../BalanceSheetTable.types';
import { useBalanceSheetCellStyles } from './BalanceSheetCell.styles';
export interface BalanceSheetCellProps {
  /**
   * Cell style variant determining visual treatment (borders, font weight)
   */
  cellStyle: BalanceSheetCellStyle;
  /**
   * Position of the cell within the table row
   */
  cellPosition: BalanceSheetCellPosition;
  /**
   * Density setting affecting padding and row height
   */
  density: BalanceSheetDensity;
  /**
   * Text alignment within the cell
   */
  alignment: 'left' | 'right';
  /**
   * Primary text content
   */
  lineOne: string;
  /** Preserve explicit line breaks and truncate long lines to the cell width. */
  multiline?: boolean;
  /**
   * Optional superscript marker rendered immediately after `lineOne`
   * (e.g. a footnote asterisk). Kept out of `lineOne` so the value itself
   * stays a clean string for negative-detection and e2e text parsing.
   */
  superscript?: string;
  /**
   * Optional secondary text content
   */
  lineTwo?: string;
  /**
   * Whether this is in a presentation context (grayscale colors)
   */
  isPresentation?: boolean;
  /**
   * Whether to show the expand/collapse icon (only for start position)
   */
  hasExpandIcon?: boolean;
  /**
   * Current expansion state
   */
  isExpanded?: boolean;
  /**
   * Callback when expand icon is clicked
   */
  onExpandClick?: () => void;
  /**
   * Indent level for nested rows (only applies to start position cells).
   * Each level adds additional left padding.
   */
  indentLevel?: number;
  /**
   * Whether this cell displays a negative value (shows in red).
   * If not provided, auto-detects based on parentheses in lineOne (e.g., "($1,000)").
   */
  isNegative?: boolean;
  /**
   * Whether start-position content aligns with rows that have an expand icon.
   * Disable this for tables that do not have expandable rows.
   */
  indentStartContent?: boolean;
  /**
   * Optional minimum width for the cell.
   * Used with flex layout to constrain how small the cell can shrink.
   */
  minWidth?: number;
  /**
   * Optional maximum width for the cell.
   * Used with flex layout to constrain how large the cell can grow.
   */
  maxWidth?: number;
  /**
   * Whether to render a 4px column gap after this cell.
   * Used for summary columns to create visual separation between sections.
   */
  hasColumnGap?: boolean;
  /**
   * Summary background variant for summary position cells.
   * - 'default': Standard summary background
   * - 'strong': Stronger background for subtotals rows
   */
  summaryVariant?: BalanceSheetSummaryVariant;
  /**
   * Optional test id applied to the cell root, used by e2e tests to read
   * specific cell values (e.g. subtotals and total net worth).
   */
  testId?: string;
  role?: 'cell';
}

/**
 * BalanceSheetCell renders a cell for the balance sheet table with
 * appropriate styling based on cell style, position, and density.
 *
 * This component supports both app (navy theme) and presentation (grayscale) contexts.
 */
/**
 * Detects if a value is negative based on accounting notation (parentheses).
 * e.g., "($1,000)" or "($1,000,000)" returns true
 */
function detectNegativeValue(value: string): boolean {
  return /^\(.*\)$/.test(value.trim());
}
export function BalanceSheetCell(t0) {
  const $ = _c(58);
  const {
    cellStyle,
    cellPosition,
    density,
    alignment,
    lineOne,
    multiline: t1,
    superscript,
    lineTwo,
    isPresentation: t2,
    hasExpandIcon: t3,
    isExpanded: t4,
    onExpandClick,
    indentLevel: t5,
    isNegative,
    indentStartContent: t6,
    minWidth,
    maxWidth,
    hasColumnGap: t7,
    summaryVariant,
    testId,
    role
  } = t0;
  const multiline = t1 === undefined ? false : t1;
  const isPresentation = t2 === undefined ? false : t2;
  const hasExpandIcon = t3 === undefined ? false : t3;
  const isExpanded = t4 === undefined ? false : t4;
  const indentLevel = t5 === undefined ? 0 : t5;
  const indentStartContent = t6 === undefined ? true : t6;
  const hasColumnGap = t7 === undefined ? false : t7;
  let t8;
  if ($[0] !== isNegative || $[1] !== lineOne) {
    t8 = isNegative ?? detectNegativeValue(lineOne);
    $[0] = isNegative;
    $[1] = lineOne;
    $[2] = t8;
  } else {
    t8 = $[2];
  }
  const computedIsNegative = t8;
  let t9;
  if ($[3] !== alignment || $[4] !== cellPosition || $[5] !== cellStyle || $[6] !== computedIsNegative || $[7] !== density || $[8] !== hasExpandIcon || $[9] !== indentLevel || $[10] !== indentStartContent || $[11] !== isPresentation || $[12] !== maxWidth || $[13] !== minWidth || $[14] !== summaryVariant) {
    t9 = {
      cellStyle,
      cellPosition,
      density,
      alignment,
      isPresentation,
      indentLevel,
      isNegative: computedIsNegative,
      indentStartContent,
      minWidth,
      maxWidth,
      summaryVariant,
      hasExpandIcon
    };
    $[3] = alignment;
    $[4] = cellPosition;
    $[5] = cellStyle;
    $[6] = computedIsNegative;
    $[7] = density;
    $[8] = hasExpandIcon;
    $[9] = indentLevel;
    $[10] = indentStartContent;
    $[11] = isPresentation;
    $[12] = maxWidth;
    $[13] = minWidth;
    $[14] = summaryVariant;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  const {
    classes,
    cx
  } = useBalanceSheetCellStyles(t9);
  let t10;
  if ($[16] !== onExpandClick) {
    t10 = e => {
      e.stopPropagation();
      onExpandClick?.();
    };
    $[16] = onExpandClick;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  const handleExpandClick = t10;
  let t11;
  if ($[18] !== cellPosition || $[19] !== classes.expandIcon || $[20] !== classes.expandIconExpanded || $[21] !== cx || $[22] !== handleExpandClick || $[23] !== hasExpandIcon || $[24] !== isExpanded || $[25] !== isPresentation) {
    t11 = cellPosition === "start" && hasExpandIcon && !isPresentation && <Box className={cx(classes.expandIcon, {
      [classes.expandIconExpanded]: isExpanded
    })} onClick={handleExpandClick} role="button" aria-label={isExpanded ? "Collapse row" : "Expand row"} aria-expanded={isExpanded}><ChevronRightIcon size={16} /></Box>;
    $[18] = cellPosition;
    $[19] = classes.expandIcon;
    $[20] = classes.expandIconExpanded;
    $[21] = cx;
    $[22] = handleExpandClick;
    $[23] = hasExpandIcon;
    $[24] = isExpanded;
    $[25] = isPresentation;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  let t12;
  if ($[27] !== multiline) {
    t12 = multiline ? {
      whiteSpace: "pre",
      overflow: "hidden",
      textOverflow: "ellipsis",
      maxWidth: "100%"
    } : undefined;
    $[27] = multiline;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  let t13;
  if ($[29] !== superscript) {
    t13 = superscript && <sup>{superscript}</sup>;
    $[29] = superscript;
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  let t14;
  if ($[31] !== classes.lineOne || $[32] !== lineOne || $[33] !== t12 || $[34] !== t13) {
    t14 = <Box component="span" className={classes.lineOne} style={t12}>{lineOne}{t13}</Box>;
    $[31] = classes.lineOne;
    $[32] = lineOne;
    $[33] = t12;
    $[34] = t13;
    $[35] = t14;
  } else {
    t14 = $[35];
  }
  let t15;
  if ($[36] !== classes.primaryContent || $[37] !== t11 || $[38] !== t14) {
    t15 = <Box className={classes.primaryContent}>{t11}{t14}</Box>;
    $[36] = classes.primaryContent;
    $[37] = t11;
    $[38] = t14;
    $[39] = t15;
  } else {
    t15 = $[39];
  }
  let t16;
  if ($[40] !== classes.lineTwo || $[41] !== lineTwo) {
    t16 = lineTwo && <Box component="span" className={classes.lineTwo}>{lineTwo}</Box>;
    $[40] = classes.lineTwo;
    $[41] = lineTwo;
    $[42] = t16;
  } else {
    t16 = $[42];
  }
  let t17;
  if ($[43] !== classes.content || $[44] !== t15 || $[45] !== t16) {
    t17 = <Box className={classes.content}>{t15}{t16}</Box>;
    $[43] = classes.content;
    $[44] = t15;
    $[45] = t16;
    $[46] = t17;
  } else {
    t17 = $[46];
  }
  let t18;
  if ($[47] !== classes.root || $[48] !== role || $[49] !== t17 || $[50] !== testId) {
    t18 = <Box className={classes.root} data-testid={testId} role={role}>{t17}</Box>;
    $[47] = classes.root;
    $[48] = role;
    $[49] = t17;
    $[50] = testId;
    $[51] = t18;
  } else {
    t18 = $[51];
  }
  let t19;
  if ($[52] !== classes.columnGap || $[53] !== hasColumnGap) {
    t19 = hasColumnGap && <Box className={classes.columnGap} />;
    $[52] = classes.columnGap;
    $[53] = hasColumnGap;
    $[54] = t19;
  } else {
    t19 = $[54];
  }
  let t20;
  if ($[55] !== t18 || $[56] !== t19) {
    t20 = <>{t18}{t19}</>;
    $[55] = t18;
    $[56] = t19;
    $[57] = t20;
  } else {
    t20 = $[57];
  }
  return t20;
}